<script setup>
import { useListId } from '@/composables/useListId.js'
import { useWishlistStore } from '@/stores/wishlist.js'
import { computed, defineAsyncComponent, ref } from 'vue'

const LoadingOverlay = defineAsyncComponent(
  () => import('@/components/basic/atoms/LoadingOverlay.vue')
)
const SvgWrapper = defineAsyncComponent(
  () => import('@/components/basic/atoms/SvgWrapper.vue')
)

const getModules = (() => {
  let cache
  return async () => {
    if (cache) return cache
    if (props.modules) return props.modules
    const { default: GtmWishlistPage } =
      await import('@/services/analytics/handlers/GtmWishlistPage')
    cache = { GtmWishlistPage }
    return cache
  }
})()

const props = defineProps({
  product: Object,
  size: { type: [Number, String], default: 34 },
  index: { type: Number, required: false },
  modules: { type: Object, required: false }
})

const el = ref(null)
const { itemListId } = useListId(el)
const wishlist = useWishlistStore()

const wishlistItem = computed(() =>
  wishlist.addedRecently(props.product.product_id, props.product.campaign_id)
)

const itemId = computed(
  () => wishlistItem.value?.id || props.product.wishlist_item_id
)

const loading = computed(() => wishlist.inProgress)

const elbAction = computed(() =>
  itemId.value
    ? 'click:remove_from_wishlist(product)'
    : 'click:add_to_wishlist(product)'
)

const handle = async () => {
  if (wishlist.inProgress) return

  if (itemId.value) {
    await wishlist.removeItem({
      id: itemId.value,
      product_id: props.product.product_id,
      campaign_id: props.product.campaign_id
    })
  } else {
    await wishlist.addItem({
      product_id: props.product.product_id,
      campaign_id: props.product.campaign_id,
      price: props.product.price.final ?? props.product.price.original
    })
  }

  const { GtmWishlistPage } = await getModules()
  if (itemId.value) {
    GtmWishlistPage.deleteWishlistItem(props.product, itemListId.value)
  } else {
    GtmWishlistPage.addItemToWishlist(props.product, itemListId.value)
  }
}
</script>

<template>
  <button
    type="button"
    class="add-to-wishlist d-flex align-center justify-space-between pointer-events-all"
    data-elbcontext="shopping:intent"
    :data-elbaction="elbAction"
    @click="handle"
    ref="el"
  >
    <span v-if="loading">
      <LoadingOverlay
        :width="size"
        :height="size"
        color="gray"
        :use-background="false"
        class="position-relative"
        :dimensions="size"
      />
    </span>
    <span v-else>
      <SvgWrapper
        v-if="itemId"
        use="icon_svg_heart_filled"
        :dimensions="size"
      />
      <SvgWrapper v-else use="icon_svg_heart" :dimensions="size" />
    </span>
    <span class="text px-1 font-weight-bold font-size-s">
      {{ translate(itemId ? 'on_wishlist' : 'add_to_wishlist') }}
    </span>
  </button>
</template>

<style scoped>
.text {
  min-width: 60px;
}
</style>
